import './update-bespoke-rating.scss'

import { notification } from 'antd'
import { Formik, FormikHelpers } from 'formik'
import { DatePicker, Form, FormItem, InputNumber, SubmitButton } from 'formik-antd'
import React, { useEffect, useState } from 'react'
import { Column, Row } from 'react-display-flex'

import { InputCurrency } from '../../../../components'
import { renderDate } from '../../../../components/tables/table-column-renderers'
import { config } from '../../../../config'
import { convertEntityValuesToFormValues } from '../../../../util/convert-entity-values-to-form-values'
import { httpClient } from '../../../../util/http-client'
import { messages } from '../messages'
import { BespokeRating } from '../models'
import { updateBespokeRatingInitialValues, UpdateBespokeRatingValues } from './models'
import { updateBespokeRatingValidationSchema } from './validators'

interface UpdateBespokeRatingProps {
  domain: string
  bespokeRating: BespokeRating
  onSuccess?: () => void
}

export const UpdateBespokeRating = ({ domain, bespokeRating, onSuccess }: UpdateBespokeRatingProps) => {
  const [initialValues, setInitialValues] = useState(updateBespokeRatingInitialValues)

  useEffect(() => {
    if (!bespokeRating) return
    setInitialValues(convertEntityValuesToFormValues(updateBespokeRatingInitialValues, bespokeRating))
  }, [bespokeRating])

  const onSubmit = async (
    formData: UpdateBespokeRatingValues,
    { setSubmitting }: FormikHelpers<UpdateBespokeRatingValues>,
  ) => {
    try {
      const url = `/banking-groups/${bespokeRating?.bankingGroupId}/compliance-rules/${bespokeRating?.id}`
      await httpClient(url, { method: 'PUT', domain, data: formData })
      notification.success({ message: messages.common.success, description: messages.create.success })
      onSuccess && onSuccess()
    } catch (error) {
      notification.error({ message: messages.common.error, description: error.error_message || messages.create.error })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={updateBespokeRatingValidationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      <Form name="update-bespoke-rating">
        <Row justifyContentSpaceBetween>
          <dl>
            <dt>ID</dt>
            <dd>{bespokeRating?.id}</dd>
          </dl>
          <dl>
            <dt>Banking Group</dt>
            <dd>{`${bespokeRating?.bankingGroupId} - ${bespokeRating?.name}`}</dd>
          </dl>
          <dl>
            <dt>Current Start Date</dt>
            <dd>{renderDate(new Date(bespokeRating?.startDate))}</dd>
          </dl>
        </Row>
        <FormItem name="startDate" label="Start Date" required>
          <DatePicker
            name="startDate"
            aria-label="startDate"
            placeholder="Start Date"
            format={config.defaultDateFormat}
          />
        </FormItem>
        <FormItem name="maxLimitShort" label="Max Limit Short" required>
          <InputNumber name="maxLimitShort" min={0} />
        </FormItem>
        <FormItem name="maxLimitLong" label="Max Limit Long" required>
          <InputNumber name="maxLimitLong" min={0} />
        </FormItem>
        <FormItem name="maxMoneyLimitShort" label="Max Money Limit Short" required>
          <InputCurrency name="maxMoneyLimitShort" min={0} />
        </FormItem>
        <FormItem name="maxMoneyLimitLong" label="Max Money Limit Long" required>
          <InputCurrency name="maxMoneyLimitLong" min={0} />
        </FormItem>
        <Column>
          <SubmitButton htmlType="submit" type="primary">
            Save Rating
          </SubmitButton>
        </Column>
      </Form>
    </Formik>
  )
}
